import { useIsomorphicLayoutEffect } from '@cian/react-utils';
import * as React from 'react';
import { useComposedRefs } from '../../hooks/useComposedRefs';
import { useStateMachine } from '../../hooks/useStateMachine';
import { getElementRef } from '../../utils/getElementRef';
export const Presence = props => {
  const {
    present,
    children
  } = props;
  const presence = usePresence(present);
  const child = typeof children === 'function' ? children({
    present: presence.isPresent
  }) : React.Children.only(children);
  const ref = useComposedRefs(presence.ref, getElementRef(child));
  const forceMount = typeof children === 'function';
  return forceMount || presence.isPresent ? /*#__PURE__*/React.cloneElement(child, {
    ref
  }) : null;
};
Presence.displayName = 'Presence';
const PRESENCE_STATE_MACHINE = {
  mounted: {
    UNMOUNT: 'unmounted',
    ANIMATION_OUT: 'unmountSuspended'
  },
  unmountSuspended: {
    MOUNT: 'mounted',
    ANIMATION_END: 'unmounted'
  },
  unmounted: {
    MOUNT: 'mounted'
  }
};

/* -------------------------------------------------------------------------------------------------
 * usePresence
 * -----------------------------------------------------------------------------------------------*/

function usePresence(present) {
  const [node, setNode] = React.useState();
  const stylesRef = React.useRef({});
  const prevPresentRef = React.useRef(present);
  const prevAnimationNameRef = React.useRef('none');
  const initialState = present ? 'mounted' : 'unmounted';
  const [state, send] = useStateMachine(initialState, PRESENCE_STATE_MACHINE);
  React.useEffect(() => {
    const currentAnimationName = getAnimationName(stylesRef.current);
    prevAnimationNameRef.current = state === 'mounted' ? currentAnimationName : 'none';
  }, [state]);
  useIsomorphicLayoutEffect(() => {
    const styles = stylesRef.current;
    const wasPresent = prevPresentRef.current;
    const hasPresentChanged = wasPresent !== present;
    if (hasPresentChanged) {
      const prevAnimationName = prevAnimationNameRef.current;
      const currentAnimationName = getAnimationName(styles);
      if (present) {
        send('MOUNT');
      } else if (currentAnimationName === 'none' || (styles === null || styles === void 0 ? void 0 : styles.display) === 'none') {
        // Если нет анимации выхода или элемент скрыт, анимации не будут выполняться
        // поэтому мы мгновенно демонтируем
        send('UNMOUNT');
      } else {
        /**
         * Когда `present` изменяется на `false`, мы проверяем изменения в animation-name, чтобы
         * определить, началась ли анимация. Мы выбрали этот подход (чтение
         * вычисленных стилей), потому что нет события `animationrun`, а `animationstart`
         * срабатывает после истечения `animation-delay`, что было бы слишком поздно.
         */
        const isAnimating = prevAnimationName !== currentAnimationName;
        if (wasPresent && isAnimating) {
          send('ANIMATION_OUT');
        } else {
          send('UNMOUNT');
        }
      }
      prevPresentRef.current = present;
    }
  }, [present, send]);
  useIsomorphicLayoutEffect(() => {
    if (node) {
      var _node$ownerDocument$d;
      let timeoutId;
      const ownerWindow = (_node$ownerDocument$d = node.ownerDocument.defaultView) !== null && _node$ownerDocument$d !== void 0 ? _node$ownerDocument$d : window;
      /**
       * Запуск ANIMATION_OUT во время ANIMATION_IN вызовет событие `animationcancel`
       * для ANIMATION_IN после того, как мы вошли в состояние `unmountSuspended`. Поэтому,
       * мы убеждаемся, что запускаем ANIMATION_END только для текущей активной анимации.
       */
      const handleAnimationEnd = event => {
        const currentAnimationName = getAnimationName(stylesRef.current);
        const eventAnimationName = normalizeAnimationName(event.animationName);
        const isCurrentAnimation = currentAnimationName.includes(eventAnimationName);
        if (event.target === node && isCurrentAnimation) {
          // С React 18 concurrency это обновление применяется через кадр после
          // окончания анимации, создавая вспышку видимого контента. Установив
          // режим заполнения анимации на "forwards", мы заставляем узел сохранять
          // стили последнего ключевого кадра, устраняя вспышку.
          //
          // Ранее мы сбрасывали обновление через ReactDom.flushSync, но с
          // анимациями выхода это приводило к удалению узла из DOM до того,
          // как синтетическое событие animationEnd было отправлено, что означало,
          // что пользовательские обработчики событий не вызывались.
          // https://github.com/radix-ui/primitives/pull/1849
          send('ANIMATION_END');
          if (!prevPresentRef.current) {
            const currentFillMode = node.style.animationFillMode;
            node.style.animationFillMode = 'forwards';
            // Сброс стиля после того, как узел успел демонтироваться (для случаев,
            // когда компонент решает не демонтироваться). Делать это раньше, чем `setTimeout`
            // (например, с `requestAnimationFrame`) все еще вызывает вспышку.
            timeoutId = ownerWindow.setTimeout(() => {
              if (node.style.animationFillMode === 'forwards') {
                node.style.animationFillMode = currentFillMode;
              }
            });
          }
        }
      };
      const handleAnimationStart = event => {
        if (event.target === node) {
          // если произошла анимация, сохраняем ее имя как предыдущее.
          prevAnimationNameRef.current = getAnimationName(stylesRef.current);
        }
      };
      node.addEventListener('animationstart', handleAnimationStart);
      node.addEventListener('animationcancel', handleAnimationEnd);
      node.addEventListener('animationend', handleAnimationEnd);
      return () => {
        ownerWindow.clearTimeout(timeoutId);
        node.removeEventListener('animationstart', handleAnimationStart);
        node.removeEventListener('animationcancel', handleAnimationEnd);
        node.removeEventListener('animationend', handleAnimationEnd);
      };
    } else {
      // Переход в состояние unmounted, если узел удален преждевременно.
      // Мы избегаем делать это во время очистки, так как узел может измениться, но все еще существовать.
      send('ANIMATION_END');
      return;
    }
  }, [node, send]);
  return {
    isPresent: ['mounted', 'unmountSuspended'].includes(state),
    ref: React.useCallback(node => {
      if (node) {
        stylesRef.current = getComputedStyle(node);
      }
      setNode(node);
    }, [])
  };
}
function getAnimationName(styles) {
  const animationName = styles === null || styles === void 0 ? void 0 : styles.animationName;
  return animationName ? normalizeAnimationName(animationName) : 'none';
}
function normalizeAnimationName(value) {
  return value.replace(/--.*?module__/g, '--').replace(/\\/g, '');
}